import { useState, useEffect, useCallback } from "react";

// Generic checkbox-selection state for a table page.
//   pageIds: array of the row ids currently visible on screen.
// Selection auto-clears whenever the visible set changes (page / search /
// filter / after a delete) so the header checkbox always reflects what's shown.
export function useTableSelection(pageIds) {
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  // Reset selection when the visible ids change (compared by value, not ref).
  const idsKey = pageIds.join(",");
  useEffect(() => {
    setSelectedIds(new Set());
  }, [idsKey]);

  const toggleRow = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const allSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const someSelected =
    pageIds.some((id) => selectedIds.has(id)) && !allSelected;

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (pageIds.every((id) => next.has(id))) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [pageIds]);

  const clear = useCallback(() => setSelectedIds(new Set()), []);

  return {
    selectedIds,
    toggleRow,
    toggleAll,
    clear,
    allSelected,
    someSelected,
  };
}
